/**
 * Public Knowledge Base
 * Browse and search help articles
 */

import React, { useState, useEffect } from 'react';
import {
  BookOpen,
  Search,
  Folder,
  Eye,
  ThumbsUp,
  ThumbsDown,
  Clock,
  TrendingUp,
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';

interface Category {
  id: number;
  name: string;
  description: string | null;
  article_count: number;
}

interface Article {
  id: number;
  category_id: number;
  title: string;
  slug: string;
  excerpt: string | null;
  content?: string;
  views: number;
  helpful_votes: number;
  not_helpful_votes: number;
  tags?: string[];
  related_articles?: number[];
  published_at: string;
}

export const KnowledgeBase: React.FC = () => {
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();

  const [categories, setCategories] = useState<Category[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<Article[]>([]);
  const [popularArticles, setPopularArticles] = useState<Article[]>([]);
  const [recentArticles, setRecentArticles] = useState<Article[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | ''>('');
  const [loading, setLoading] = useState(false);
  const [hasVoted, setHasVoted] = useState(false);

  useEffect(() => {
    loadCategories();
    loadPopularArticles();
    loadRecentArticles();

    if (slug) {
      loadArticle(slug);
    } else {
      loadArticles();
    }
  }, [slug]);

  const loadCategories = async () => {
    try {
      const response = await fetch('/api/v1/public/kb/categories');
      const data = await response.json();
      if (data.success) {
        setCategories(data.data.categories);
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadArticles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category_id', selectedCategory.toString());

      const response = await fetch(`/api/v1/public/kb/articles?${params}`);
      const data = await response.json();
      if (data.success) {
        setArticles(data.data.articles);
      }
    } catch (error) {
      console.error('Failed to load articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadArticle = async (identifier: string) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/public/kb/articles/${identifier}`);
      const data = await response.json();
      if (data.success) {
        setSelectedArticle(data.data.article);

        // Load related articles
        if (data.data.article.related_articles && data.data.article.related_articles.length > 0) {
          loadRelatedArticles(data.data.article.related_articles);
        }
      }
    } catch (error) {
      console.error('Failed to load article:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedArticles = async (articleIds: number[]) => {
    try {
      const promises = articleIds.map((id) =>
        fetch(`/api/v1/public/kb/articles/${id}`).then((r) => r.json())
      );
      const results = await Promise.all(promises);
      const related = results
        .filter((r) => r.success)
        .map((r) => r.data.article);
      setRelatedArticles(related);
    } catch (error) {
      console.error('Failed to load related articles:', error);
    }
  };

  const loadPopularArticles = async () => {
    try {
      const response = await fetch('/api/v1/public/kb/popular?limit=5');
      const data = await response.json();
      if (data.success) {
        setPopularArticles(data.data.articles);
      }
    } catch (error) {
      console.error('Failed to load popular articles:', error);
    }
  };

  const loadRecentArticles = async () => {
    try {
      const response = await fetch('/api/v1/public/kb/recent?limit=5');
      const data = await response.json();
      if (data.success) {
        setRecentArticles(data.data.articles);
      }
    } catch (error) {
      console.error('Failed to load recent articles:', error);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setLoading(true);
    try {
      const params = new URLSearchParams({ q: searchQuery });
      if (selectedCategory) params.append('category_id', selectedCategory.toString());

      const response = await fetch(`/api/v1/public/kb/search?${params}`);
      const data = await response.json();
      if (data.success) {
        setArticles(data.data.articles);
        setSelectedArticle(null);
        navigate('/kb');
      }
    } catch (error) {
      console.error('Failed to search articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (voteType: 'helpful' | 'not_helpful') => {
    if (!selectedArticle || hasVoted) return;

    try {
      const response = await fetch(`/api/v1/public/kb/articles/${selectedArticle.id}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vote_type: voteType }),
      });

      const data = await response.json();
      if (data.success) {
        setHasVoted(true);
        // Reload article to get updated vote counts
        loadArticle(selectedArticle.slug);
      }
    } catch (error) {
      console.error('Failed to vote:', error);
    }
  };

  const handleCategoryFilter = (categoryId: number | '') => {
    setSelectedCategory(categoryId);
    setSelectedArticle(null);
    navigate('/kb');
  };

  useEffect(() => {
    if (selectedCategory !== '') {
      loadArticles();
    }
  }, [selectedCategory]);

  // Article view
  if (selectedArticle) {
    const category = categories.find((c) => c.id === selectedArticle.category_id);
    const totalVotes = selectedArticle.helpful_votes + selectedArticle.not_helpful_votes;
    const helpfulPercentage =
      totalVotes > 0 ? Math.round((selectedArticle.helpful_votes / totalVotes) * 100) : 0;

    return (
      <div className="min-h-screen bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Back button */}
          <button
            onClick={() => {
              setSelectedArticle(null);
              navigate('/kb');
            }}
            className="text-orange-400 hover:text-orange-300 mb-6 flex items-center gap-2"
          >
            ← Back to Knowledge Base
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main content */}
            <div className="lg:col-span-2">
              <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6">
                {/* Category badge */}
                {category && (
                  <div className="mb-4">
                    <span className="px-3 py-1 bg-orange-900 text-orange-300 rounded-full text-sm">
                      {category.name}
                    </span>
                  </div>
                )}

                {/* Title */}
                <h1 className="text-3xl font-bold text-white mb-4">
                  {selectedArticle.title}
                </h1>

                {/* Meta info */}
                <div className="flex items-center gap-4 text-slate-400 text-sm mb-6 pb-6 border-b border-slate-700/50">
                  <div className="flex items-center gap-1">
                    <Eye className="w-4 h-4" />
                    {selectedArticle.views} views
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {new Date(selectedArticle.published_at).toLocaleDateString()}
                  </div>
                </div>

                {/* Content */}
                <div
                  className="prose prose-invert max-w-none mb-8"
                  dangerouslySetInnerHTML={{ __html: selectedArticle.content || '' }}
                />

                {/* Tags */}
                {selectedArticle.tags && selectedArticle.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-8">
                    {selectedArticle.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-slate-700/50 text-slate-300 rounded-full text-sm"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Voting */}
                <div className="bg-gray-750 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-white mb-4">
                    Was this article helpful?
                  </h3>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => handleVote('helpful')}
                      disabled={hasVoted}
                      className={`flex items-center gap-2 px-6 py-3 rounded-lg transition-colors ${
                        hasVoted
                          ? 'bg-slate-700/50 text-slate-500 cursor-not-allowed'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      <ThumbsUp className="w-5 h-5" />
                      Yes ({selectedArticle.helpful_votes})
                    </button>
                    <button
                      onClick={() => handleVote('not_helpful')}
                      disabled={hasVoted}
                      className={`flex items-center gap-2 px-6 py-3 rounded-lg transition-colors ${
                        hasVoted
                          ? 'bg-slate-700/50 text-slate-500 cursor-not-allowed'
                          : 'bg-red-600 text-white hover:bg-red-700'
                      }`}
                    >
                      <ThumbsDown className="w-5 h-5" />
                      No ({selectedArticle.not_helpful_votes})
                    </button>
                    <span className="text-slate-400 ml-4">
                      {helpfulPercentage}% found this helpful
                    </span>
                  </div>
                  {hasVoted && (
                    <p className="text-green-400 mt-4">Thank you for your feedback!</p>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Related articles */}
              {relatedArticles.length > 0 && (
                <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6">
                  <h3 className="text-lg font-medium text-white mb-4">Related Articles</h3>
                  <div className="space-y-3">
                    {relatedArticles.map((article) => (
                      <button
                        key={article.id}
                        onClick={() => navigate(`/kb/${article.slug}`)}
                        className="block w-full text-left p-3 bg-gray-750 hover:bg-slate-700/50 rounded-lg transition-colors"
                      >
                        <div className="text-white font-medium">{article.title}</div>
                        <div className="text-sm text-slate-400 mt-1 flex items-center gap-2">
                          <Eye className="w-3 h-3" />
                          {article.views} views
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Popular articles */}
              {popularArticles.length > 0 && (
                <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-orange-400" />
                    Popular Articles
                  </h3>
                  <div className="space-y-3">
                    {popularArticles.slice(0, 3).map((article) => (
                      <button
                        key={article.id}
                        onClick={() => navigate(`/kb/${article.slug}`)}
                        className="block w-full text-left p-3 bg-gray-750 hover:bg-slate-700/50 rounded-lg transition-colors"
                      >
                        <div className="text-white font-medium text-sm">
                          {article.title}
                        </div>
                        <div className="text-xs text-slate-400 mt-1 flex items-center gap-2">
                          <Eye className="w-3 h-3" />
                          {article.views} views
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="min-h-screen bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="p-4 bg-orange-500 rounded-full">
              <BookOpen className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">Knowledge Base</h1>
          <p className="text-slate-400 text-lg">
            Find answers to your questions
          </p>
        </div>

        {/* Search */}
        <div className="max-w-2xl mx-auto mb-8">
          <form onSubmit={handleSearch}>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for articles..."
                className="w-full pl-12 pr-4 py-4 bg-slate-800/80 border border-slate-700/50 rounded-lg text-white text-lg focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
              />
            </div>
          </form>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Categories */}
            <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 mb-6">
              <h3 className="text-lg font-medium text-white mb-4">Categories</h3>
              <div className="space-y-2">
                <button
                  onClick={() => handleCategoryFilter('')}
                  className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${
                    selectedCategory === ''
                      ? 'bg-orange-500 text-white'
                      : 'text-slate-300 hover:bg-slate-700/50'
                  }`}
                >
                  All Articles
                </button>
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryFilter(category.id)}
                    className={`w-full text-left px-4 py-2 rounded-lg transition-colors flex items-center justify-between ${
                      selectedCategory === category.id
                        ? 'bg-orange-500 text-white'
                        : 'text-slate-300 hover:bg-slate-700/50'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <Folder className="w-4 h-4" />
                      {category.name}
                    </span>
                    <span className="text-sm text-slate-400">
                      {category.article_count}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Recent articles */}
            {recentArticles.length > 0 && (
              <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-medium text-white mb-4">Recent Articles</h3>
                <div className="space-y-3">
                  {recentArticles.map((article) => (
                    <button
                      key={article.id}
                      onClick={() => navigate(`/kb/${article.slug}`)}
                      className="block w-full text-left p-3 bg-gray-750 hover:bg-slate-700/50 rounded-lg transition-colors"
                    >
                      <div className="text-white font-medium text-sm">
                        {article.title}
                      </div>
                      <div className="text-xs text-slate-400 mt-1">
                        {new Date(article.published_at).toLocaleDateString()}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Main content */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="text-center py-12 text-slate-400">Loading...</div>
            ) : articles.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-slate-400 mb-4">No articles found</div>
                {searchQuery && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      loadArticles();
                    }}
                    className="text-orange-400 hover:text-orange-300"
                  >
                    Clear search
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {articles.map((article) => {
                  const category = categories.find((c) => c.id === article.category_id);

                  return (
                    <button
                      key={article.id}
                      onClick={() => navigate(`/kb/${article.slug}`)}
                      className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 text-left hover:bg-slate-700/30 transition-colors"
                    >
                      {category && (
                        <span className="px-2 py-1 bg-orange-900 text-orange-300 rounded text-xs">
                          {category.name}
                        </span>
                      )}
                      <h3 className="text-xl font-medium text-white mt-3 mb-2">
                        {article.title}
                      </h3>
                      {article.excerpt && (
                        <p className="text-slate-400 text-sm mb-4 line-clamp-2">
                          {article.excerpt}
                        </p>
                      )}
                      <div className="flex items-center gap-4 text-slate-500 text-sm">
                        <div className="flex items-center gap-1">
                          <Eye className="w-4 h-4" />
                          {article.views}
                        </div>
                        <div className="flex items-center gap-1">
                          <ThumbsUp className="w-4 h-4" />
                          {article.helpful_votes}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBase;
